import { z } from "zod";

export const FORECAST_RUN_PURPOSES = [
  "production",
  "backtest",
  "scenario",
  "single_series",
] as const;
export const FORECAST_RUN_STATUSES = [
  "running",
  "completed",
  /**
   * Wrote forecasts, but at least one division was held back by a
   * broken-input guard (lead-time coverage or demand-key coverage) —
   * `errorMessage` names which.
   */
  "partial",
  "failed",
  "refused",
] as const;

export const forecastRunPurposeSchema = z.enum(FORECAST_RUN_PURPOSES);
export const forecastRunStatusSchema = z.enum(FORECAST_RUN_STATUSES);
export type ForecastRunPurpose = z.infer<typeof forecastRunPurposeSchema>;
export type ForecastRunStatus = z.infer<typeof forecastRunStatusSchema>;

export function isForecastRunPurpose(
  value: string,
): value is ForecastRunPurpose {
  return forecastRunPurposeSchema.safeParse(value).success;
}

export function isForecastRunStatus(value: string): value is ForecastRunStatus {
  return forecastRunStatusSchema.safeParse(value).success;
}

/** Wire form of a DATEONLY value. The branded backend type does not cross packages. */
export const asOfDateSchema = z.iso.date();

/**
 * The method config a forecast run records on its manifest
 * (`im_forecast_runs.method_config`), i.e. the four settings that are not
 * already their own columns. Strict: resuming a run rebuilds its context from
 * this, and a run that did not record all four (an older or seeded one) has
 * no settings to show.
 */
export const forecastRunMethodConfigSchema = z.object({
  availabilityByClass: z.object({
    smooth: z.number(),
    erratic: z.number(),
    intermittent: z.number(),
    lumpy: z.number(),
  }),
  availabilityByPopularity: z.record(z.string(), z.number()),
  leadTimeDefaultDays: z.number(),
  maxUnobservedLeadTimeShare: z.number(),
});
export type ForecastRunMethodConfig = z.infer<
  typeof forecastRunMethodConfigSchema
>;

export const forecastRunSummarySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  asOfDate: asOfDateSchema,
  purpose: forecastRunPurposeSchema,
  status: forecastRunStatusSchema,
  configHash: z.string().length(64),
});
export type ForecastRunSummary = z.infer<typeof forecastRunSummarySchema>;
